import axios from "axios"
import { isViewHelpBlocked } from "@/services/viewHelp/guard"
import * as Sentry from "@sentry/vue"
import router from "@/router"
import { useAuthStore } from "@/store/authentication"
import { useSettingsStore } from "@/store/settings"
import { useSubscriptionStore } from "@/store/subscription"
import { useNotificationStore } from "@/store/notification"
import { getViolationMessage } from "@/services/violations"
import { i18n } from "@/plugins/i18n"

// Route names on which we must NOT force a paywall redirect (would loop / block recovery).
const LOCKOUT_SAFE_ROUTES = [
  "Checkout",
  "CheckoutReturn",
  "Plans",
  "Login",
  "Registration",
  "ForgotPassword",
  "ResetPassword",
  "SuccessPassword",
]

// Requests where a 401 means "these credentials are wrong", not "your session ended" — the Login view
// renders that inline, and a logout that 401s already ends up on Login by itself.
const CREDENTIAL_REQUEST_PATHS = ["/login", "/register", "/logout"]

// Violations whose callers render their own recovery UI: useSeatLimitGuard offers to buy seats,
// ChangePlanView explains a declined card. SUBSCRIPTION_REQUIRED and FEATURE_NOT_ENTITLED need no
// entry — both are redirected away below, to the paywall and to the add-on upsell respectively.
const SELF_HANDLED_VIOLATIONS = ["SEAT_LIMIT_REACHED", "PAYMENT_DECLINED"]
// The statuses ErrorHandlingControllerAdvice uses to say "your change was rejected".
// 415 belongs here for the same reason as the rest: it is a write the server refused and the person
// who made it has to be told. It was missing, so an upload rejected for its content type failed in
// silence — the file simply never appeared.
const REJECTED_WRITE_STATUSES = [400, 403, 404, 415, 422]
const WRITE_METHODS = ["post", "put", "patch", "delete"]

// A 400/422 means the request did not match the API contract — a body the backend could not read, or a
// field this client should have validated first. 403/404/409 are ordinary outcomes and stay out of Sentry.
const CONTRACT_BUG_STATUSES = [400, 422]
// The advice's codes for exactly those. Any other violation on a 400 is a business rule the user tripped
// over, already explained on screen; reporting those would bury the real defects.
const CONTRACT_BUG_VIOLATIONS = ["MALFORMED_REQUEST", "INVALID_FIELD"]

/**
 * Whether a failure is this client's fault rather than a legitimate refusal. Rejections carrying no code
 * count: that is what Spring answers when nothing handled the exception, the case that used to leave a
 * rejected save with no trace in Sentry or in the backend log.
 */
const isContractBug = error => {
  if (!CONTRACT_BUG_STATUSES.includes(error.response?.status)) return false
  const code = error.response?.data?.violationCode
  return !code || CONTRACT_BUG_VIOLATIONS.includes(code)
}

// Requests in flight when the page goes away abort as responseless failures that say nothing about
// the backend.
let unloading = false
window.addEventListener("pagehide", () => {
  unloading = true
})

/**
 * Whether the server never answered (`ECONNABORTED` timeout, `ERR_NETWORK` refused/reset) — a backend
 * that hangs or dies mid-request, which the status-based branch below cannot see.
 *
 * The exclusions are the ways this happens with no defect behind it, all common on a building site:
 * cancelled by the app, cut off by page unload, sent while the browser knew it was offline, or sent
 * in the `background` with nobody asking. That last one is mostly a phone waking up: the tab turns
 * visible, the polls fire at once, and the network is not back yet while `navigator.onLine` already
 * says it is. The next tick asks again, and an outage that lasts shows on the first request somebody
 * did ask for.
 */
const isServerUnreachable = error =>
  !error.response && !axios.isCancel(error) && !unloading && !error.config?.background && navigator.onLine !== false

/**
 * The trace this request belongs to, or null when there is none. The same id rides to the API in
 * `sentry-trace`, so `trace:<id>` in Sentry finds the browser event, the backend issue and the replay
 * together — which is why the user is shown it.
 */
const currentTraceId = () => Sentry.getTraceData()["sentry-trace"]?.split("-")[0] ?? null

// A project body nests deep enough that an unbounded walk would fill the event with noise.
const MAX_REPORTED_EMPTY_PATHS = 40

/**
 * Every path in [value] that carries nothing ("configuration.defaultCurrency", "phases[0].endDate").
 *
 * Paths only, never the values at them — this rides along to Sentry and a project body is full of
 * customer data. A null against a non-null Kotlin property is the usual reason a save is refused, so
 * the nulls we sent often identify the culprit whatever the backend managed to say.
 */
const emptyPathsIn = (value, path = "", found = []) => {
  if (found.length >= MAX_REPORTED_EMPTY_PATHS) return found
  if (value === null || value === undefined) {
    if (path) found.push(path)
  } else if (Array.isArray(value)) {
    value.forEach((item, index) => emptyPathsIn(item, `${path}[${index}]`, found))
  } else if (typeof value === "object") {
    Object.entries(value).forEach(([key, item]) => emptyPathsIn(item, path ? `${path}.${key}` : key, found))
  }
  return found
}

/**
 * The body this request sent, parsed, or undefined when there is none to read.
 */
const bodySent = error => {
  // Multipart uploads arrive as FormData, which axios never serialises to a string.
  if (typeof error.config?.data !== "string") return undefined
  try {
    return JSON.parse(error.config.data)
  } catch {
    return undefined
  }
}

const isPlainObject = value => value !== null && typeof value === "object" && !Array.isArray(value)

// Same reasoning as the empty-path cap: a summary, not a dump of the body.
const MAX_REPORTED_MISSING_KEYS = 20

/**
 * Every key that some elements of an array carry and their siblings do not ("users[].isLeading").
 *
 * The blind spot `emptyPathsIn` cannot see. A property left `undefined` is dropped by
 * JSON.stringify, so an object that never had the field looks, on the wire, exactly like one that
 * was never supposed to have it — and Jackson answers a missing non-null property with a 400 whose
 * reason ("Missing required creator property …") production does not put in the response body.
 * Comparing an array's elements against each other reconstructs it: the flag every other user in
 * the same list carries is the one this save left out.
 *
 * Key names only, never values — this rides along to Sentry, like the empty paths above.
 */
const missingSiblingKeys = (value, path = "", found = []) => {
  if (found.length >= MAX_REPORTED_MISSING_KEYS) return found
  if (Array.isArray(value)) {
    const objects = value.filter(isPlainObject)
    if (objects.length > 1) {
      const keys = new Set(objects.flatMap(object => Object.keys(object)))
      keys.forEach(key => {
        if (found.length < MAX_REPORTED_MISSING_KEYS && objects.some(object => !(key in object))) {
          found.push(`${path}[].${key}`)
        }
      })
    }
    value.forEach((item, index) => missingSiblingKeys(item, `${path}[${index}]`, found))
  } else if (isPlainObject(value)) {
    Object.entries(value).forEach(([key, item]) => missingSiblingKeys(item, path ? `${path}.${key}` : key, found))
  }
  return found
}

/**
 * The endpoint a URL belongs to, ids removed ("/project/9f3c…/phases" → "/project/:id/phases").
 *
 * For Sentry grouping only. Axios gives every failure the same message ("Request failed with status code
 * 400"), so unfingerprinted they collapse into one issue; keeping the ids splits them one per project
 * instead. Every route segment in this API is alphabetic, so a digit marks a value.
 */
const routeOf = url =>
  (url ?? "")
    .split("?")[0]
    .split("/")
    .map(segment => (/\d/.test(segment) ? ":id" : segment))
    .join("/")

// Enough of the server's wording to recognise the defect, not enough to paste a stack trace into a tag.
const MAX_REPORTED_SERVER_MESSAGE = 200

/**
 * What the server said in its own words, if anything.
 *
 * Three bodies can arrive here: our ErrorResponse (a violationCode, no prose), Spring's default for an
 * exception nothing handled (`error`, and `message` only where the deployment includes it), or a bare
 * string. Often that amounts to "Bad Request" — but when it does not, it names the defect outright,
 * which is worth reading before anything else in the event.
 */
const serverSaid = error => {
  const data = error.response?.data
  if (typeof data === "string") return data.slice(0, MAX_REPORTED_SERVER_MESSAGE)
  const said = data?.message ?? data?.detail ?? data?.error
  return typeof said === "string" ? said.slice(0, MAX_REPORTED_SERVER_MESSAGE) : undefined
}

/**
 * The one-line identity of a failure, for the issue title ("PUT /project/:id → 400 INVALID_FIELD").
 *
 * Axios stamps every rejection with the same message, so untouched the issue list reads as a wall of
 * "Request failed with status code 400" and an alert mail says nothing at all. The fingerprint already
 * keeps these apart; this only makes the split legible.
 */
const titleFor = error => {
  const method = (error.config?.method ?? "?").toUpperCase()
  // A responseless failure has no status to name, but its axios code says refused from timed out.
  const outcome = error.response?.status ?? error.code ?? "no response"
  const violation = error.response?.data?.violationCode
  return `${method} ${routeOf(error.config?.url)} → ${outcome}${violation ? ` ${violation}` : ""}`
}

/**
 * Whether a failed request should raise a user-visible notification.
 *
 * A rejected write used to look exactly like a successful one: only 5xx and 409 were reported, so a 400
 * from the advice (bean validation, or any ValidationException/DomainDeleteException) stopped the spinner
 * and produced nothing else. Reads stay quiet — a failed GET is already covered by the error views and
 * route guards, and background polling would spam the snackbar.
 *
 * Individual requests opt out with `{ silent: true }` when the caller renders the violation inline, or with
 * `{ handlesViolation: code => … }` for the codes the caller answers itself and nothing else (a removal that
 * archives what the delete guard refused, `services/archive.js`).
 */
const isReportable = error => {
  if (error.config?.silent || axios.isCancel(error)) return false
  if (error.config?.handlesViolation?.(error.response?.data?.violationCode)) return false

  const status = error.response?.status
  // No response at all: connection lost, DNS, timeout. The most important case to report on a
  // building site, and the one that used to be silent everywhere. Not for a `background` request,
  // though: nobody asked for it, so a snackbar would answer a question nobody put.
  if (status === undefined) return !error.config?.background

  if (SELF_HANDLED_VIOLATIONS.includes(error.response?.data?.violationCode)) return false
  if (status >= 500 || status === 409) return true

  return REJECTED_WRITE_STATUSES.includes(status) && WRITE_METHODS.includes(error.config?.method?.toLowerCase())
}

/**
 * What to say when nothing came back.
 *
 * This used to be one line for every method: "no connection to the server, your changes were not
 * saved". It asserts two things this code cannot know and one it can contradict.
 *
 * *No connection* is a guess at the cause. A responseless failure is any XHR `onerror` or timeout —
 * a reset mid-transfer, DNS, TLS, a request an extension or a virus scanner blocked, a server that
 * accepted the body and then hung. `isServerUnreachable` has already excluded the one case where the
 * browser genuinely knows the connection is gone (`navigator.onLine === false`), so by construction
 * this wording is used where the browser believes it is online.
 *
 * *Your changes were not saved* is a guess at the outcome, and the dangerous one. A request with no
 * response may never have arrived, or may have been applied in full with only the answer lost on the
 * way back — the client cannot tell those apart. Told the change did not land, the user retries, and
 * on a create that is how one upload becomes three. What we can honestly say is that we do not know,
 * and that checking beats retrying.
 *
 * A read has no such ambiguity: nothing arrived, so nothing is on screen, and there are no changes to
 * be wrong about. `isReportable` lets responseless failures through whatever the method, so a failed
 * GET reached the write wording too.
 */
const messageForNoResponse = error =>
  i18n.global.t(
    WRITE_METHODS.includes(error.config?.method?.toLowerCase())
      ? "general.error.no_response_write"
      : "general.error.no_response_read"
  )

/**
 * Best available wording for a failure, most specific first.
 *
 * Only ever wording of ours: a violationCode is a symbol we translate, whereas everything else the server
 * puts in the body — Spring's `message`, the exception class name — is written for us, not for the person
 * looking at the screen, and is not ours to hand on. A code `violations.js` does not know leaves a
 * status-specific fallback as the only thing to say, which is the right answer anyway.
 */
const messageForError = error => {
  const data = error.response?.data
  if (data?.violationCode) {
    try {
      const violationMessage = getViolationMessage(data)
      if (violationMessage) return violationMessage
    } catch (err) {
      console.warn("Failed to parse violation message:", err)
    }
  }

  const t = i18n.global.t
  switch (error.response?.status) {
    case 400:
    case 422:
      return t("general.error.invalid_request")
    case 403:
      return t("general.error.forbidden")
    case 404:
      return t("general.error.not_found")
    // A 413 normally carries FILE_TOO_LARGE and is answered above. This is the bodiless one a reverse
    // proxy raises on its own limit, before the request ever reaches the API.
    case 413:
      return t("general.error.payload_too_large")
    case undefined:
      return messageForNoResponse(error)
    default:
      return t("general.error.internal_server_error")
  }
}

const isCredentialRequest = error => CREDENTIAL_REQUEST_PATHS.includes((error.config?.url ?? "").split("?")[0])

/**
 * The failures the interceptor answers by taking the user somewhere that explains them, rather than
 * by raising a snackbar: an ended session, the paywall, the add-on upsell. Reads the same conditions
 * as the three early returns below rather than the violation codes alone, so the two cannot drift.
 */
const isRedirectedAway = error => {
  const status = error.response?.status
  const code = error.response?.data?.violationCode
  // The token is cleared whatever the route, and Login is where the next step happens.
  if (status === 401) return !isCredentialRequest(error)
  if (status === 402 || code === "SUBSCRIPTION_REQUIRED") return true
  // Without a featureSet there is nowhere to send anybody, and the failure falls through to be
  // reported like any other refused write.
  return code === "FEATURE_NOT_ENTITLED" && Boolean(error.response?.data?.violationValue?.featureSet)
}

/**
 * Whether the user has already been told what happened — by the snackbar [isReportable] raises, or by
 * being taken to a page that explains it better than a snackbar could.
 *
 * For catch blocks that have work of their own to do (putting a rejected form back on screen) and
 * used to raise a generic sentence beside it. Two notifications for one refusal is the small problem;
 * the real one is that the generic sentence buries the specific one, and it is the specific one —
 * "the working time exceeds the maximum permitted by the work-time model" — that says what to change.
 *
 * Deliberately positive, so anything it cannot vouch for answers false and the caller speaks:
 * a violation in `SELF_HANDLED_VIOLATIONS` whose caller is somebody else's screen, a `silent` request
 * whose caller renders the refusal itself, a status no branch of [isReportable] claims, and — the one
 * that is not an axios rejection at all — an error thrown while handling a *successful* response,
 * which never passed through the interceptor and which [isReportable] would otherwise mistake for a
 * failure with no response. A doubled message is a nuisance; a refusal nobody mentions is a lost day.
 *
 * A cancelled request is the exception that stays quiet: only this app cancels one, so there is
 * nobody to tell.
 */
export const isAnsweredForUser = error =>
  axios.isAxiosError(error) && (axios.isCancel(error) || isReportable(error) || isRedirectedAway(error))

// Set while the bounce to Login is in flight, so a view that fired several requests at once does not
// push one navigation per 401.
let handlingSessionExpiry = false

/**
 * End the session visibly.
 *
 * The token is gone the moment the 401 arrives; without the redirect the user keeps looking at a dead
 * page (every further request 401s too) until some later navigation happens to bounce them to Login
 * with no explanation. `reason` makes Login say why, `redirect` sends them back afterwards.
 */
const redirectToLogin = error => {
  const route = router.currentRoute.value
  // Nothing to explain on the unauthenticated screens — they are already where the bounce would land.
  if (isCredentialRequest(error) || route.meta.requiresAuth === false || handlingSessionExpiry) return

  handlingSessionExpiry = true
  router
    .replace({ name: "Login", query: { reason: "session_expired", redirect: route.fullPath } })
    .catch(() => {})
    .finally(() => {
      handlingSessionExpiry = false
    })
}

const axiosConfig = {
  baseURL: "/api",
  paramsSerializer: {
    // Serialize arrays for API compatibility
    // Converts [1, 2, 3] to ?param=1&param=2&param=3
    serialize: params => {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return
        }
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(key, item))
        } else {
          searchParams.append(key, value)
        }
      })
      return searchParams.toString()
    },
  },
}

export const HEADER_TOKEN_TENANT = "X-TENANT-ID"
export const HEADER_TOKEN_AUTHORIZATION = "authorization"
// Which client this is. The backend stamps it on every time entry it creates, as where the day was
// recorded (`WEB` here, `MOBILE` from the app). Sent on every request rather than only the time-tracking
// ones: it describes the client, not the call, and a future consumer should not have to remember it.
export const HEADER_CLIENT_CHANNEL = "X-Lewara-Client"

export const apiClient = axios.create(axiosConfig)

// A request that asked for a Blob (a PDF, a file) gets its error body as a Blob too, and everything
// below reads `violationCode` off an object: unread, every refusal would count as a contract bug and
// be worded by its status alone. Registered first, so it runs before the handler below.
apiClient.interceptors.response.use(undefined, async error => {
  const data = error.response?.data
  if (data instanceof Blob && /json/i.test(data.type)) {
    try {
      error.response.data = JSON.parse(await data.text())
    } catch {
      // Not the JSON it claimed to be: left as it came, and handled as a body without a code.
    }
  }
  return Promise.reject(error)
})

apiClient.interceptors.response.use(
  response => {
    const authStore = useAuthStore()
    const settingsStore = useSettingsStore()

    if (!authStore.getTenant() || !authStore.getToken()) {
      settingsStore.setSettings(null)
    }

    return response
  },
  error => {
    // The Erklärung's frame refuses every write (services/viewHelp/sandbox.ts). That is the sandbox
    // doing its job, so it gets neither a snackbar inside the demo nor a Sentry report.
    if (isViewHelpBlocked(error)) return Promise.reject(error)

    const authStore = useAuthStore()
    const notificationStore = useNotificationStore()

    // Handle authentication errors
    if (error.response?.status === 401) {
      authStore.setTenant("")
      authStore.clearToken()
      authStore.clearSessionData()
      redirectToLogin(error)
      return Promise.reject(error)
    }

    // Handle subscription lockout: the tenant filter returns 402 SUBSCRIPTION_REQUIRED when a
    // tenant is neither active, in grace, nor exempt. Flip the store to locked and route to the
    // paywall (unless already on a recovery route). Let SEAT_LIMIT_REACHED (403) fall through to the
    // caller, which renders its own recovery UI (buy seats).
    if (error.response?.status === 402 || error.response?.data?.violationCode === "SUBSCRIPTION_REQUIRED") {
      const subscriptionStore = useSubscriptionStore()
      subscriptionStore.status = "PAST_DUE"
      subscriptionStore.gracePeriodEnd = null
      subscriptionStore.loaded = true
      if (!LOCKOUT_SAFE_ROUTES.includes(router.currentRoute.value.name)) {
        router.replace({ name: "Checkout" })
      }
      return Promise.reject(error)
    }

    // Add-on gate: the backend returns 403 FEATURE_NOT_ENTITLED for a feature-set the tenant has not
    // bought. `featureSetGuard` normally catches this before the page even loads, so reaching here
    // means the client's entitlements were stale (plan downgraded in another session, or the status
    // load had failed and the guard stayed inert). The page the request belongs to cannot work, so
    // send the user to the same upsell the guard would have — a silently empty screen is worse.
    const lockedFeatureSet =
      error.response?.data?.violationCode === "FEATURE_NOT_ENTITLED"
        ? error.response.data.violationValue?.featureSet
        : null
    if (lockedFeatureSet) {
      // Re-read the truth we just proved stale: /principal for the guard's entitlements, status for the
      // drawer. The redirect waits on the principal — the upsell page bounces to the dashboard while
      // `isEntitled` still claims the tenant owns this.
      useSubscriptionStore().loadStatus()
      const principal = authStore.loadPrincipal()
      // A `background` request belongs to no page — the drawer's Tagesmeldungen count, say, after the workspace
      // lost the set while the user was signed in. Nobody asked for it, so the user stays where they are; the
      // fresh principal is enough for the gates to stop sending it.
      if (!error.config?.background) {
        principal.finally(() => {
          if (router.currentRoute.value.name !== "FeatureLocked") {
            router.replace({ name: "FeatureLocked", params: { featureSet: lockedFeatureSet } })
          }
        })
      }
      // No notification: the upsell page explains the block far better than a snackbar would.
      return Promise.reject(error)
    }

    // Also decides whether the snackbar offers a reference — one for an unreported failure would
    // send support looking for an issue that does not exist.
    const reported = error.response?.status >= 500 || isContractBug(error) || isServerUnreachable(error)

    // Surface the failure so a rejected write never passes for a saved one
    if (isReportable(error)) {
      notificationStore.raiseError({
        message: messageForError(error),
        status: error.response?.status ?? null,
        traceId: reported ? currentTraceId() : null,
      })
    }

    if (reported) {
      const rejectedField = error.response?.data?.violationValue?.field
      const sentBody = bodySent(error)
      const missingFields = sentBody === undefined ? undefined : missingSiblingKeys(sentBody)

      Sentry.captureException(error, scope => {
        // Sentry types this as string[] and drops the event on anything else, so no bare optional here.
        scope.setFingerprint([
          "{{ default }}",
          error.config?.method ?? "",
          routeOf(error.config?.url),
          rejectedField ?? "",
          // Responseless only, so existing 5xx issues keep their fingerprint. Axios gives these
          // all one message ("Network Error"); the code separates refused from timed out.
          ...(error.response ? [] : [error.code ?? "no_code"]),
        ])
        // Tags, not extras, for the handful of facts triage needs first: they are indexed, so
        // `api.route:/project/:id` finds every occurrence and the issue's tag breakdown answers "one
        // tenant or all of them" without opening a single event. They are also the part of an event
        // every reader — the Sentry UI, an alert, an MCP client — puts in front of you, which matters
        // here because the stack trace of an axios failure is nothing but axios internals.
        scope.setTags({
          "api.route": routeOf(error.config?.url),
          "api.method": (error.config?.method ?? "unknown").toUpperCase(),
          "api.status": String(error.response?.status ?? error.code ?? "no_response"),
          "api.violation": error.response?.data?.violationCode ?? "none",
          ...(rejectedField ? { "api.field": rejectedField } : {}),
          // The likeliest culprit of a rejected write, spelled out where it cannot be missed.
          ...(missingFields?.length ? { "api.missing_fields": missingFields.slice(0, 3).join(" ").slice(0, 200) } : {}),
        })
        scope.setExtras({
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          serverSaid: serverSaid(error),
          responseData: error.response?.data,
          rejectedField,
          missingFieldsSent: missingFields,
          emptyFieldsSent: sentBody === undefined ? undefined : emptyPathsIn(sentBody),
          requestBodyBytes: typeof error.config?.data === "string" ? error.config.data.length : null,
          code: error.code,
          timeoutMs: error.config?.timeout || null,
        })
        scope.addEventProcessor(event => {
          const thrown = event.exception?.values?.[0]
          if (thrown) thrown.value = titleFor(error)
          return event
        })
        return scope
      })
    }

    return Promise.reject(error)
  }
)

// Enables additional settings for ApiClient
// While implementing a request, the following additional options are available for the 'settings' param
// headers: { tenant: Boolean, authorization: Boolean }
// Tenant is used for emiting the Tenant-Id to the Api.
// authorization is used for emiting the Auth-Token to the Api.
// By default emits tenant-id and auth-token to the api
apiClient.interceptors.request.use(config => {
  const enableTenantInterceptor = config.headers.tenant === undefined ? true : config.headers.tenant
  const enableAuthInterceptor = config.headers.authorization === undefined ? true : config.headers.authorization
  const store = useAuthStore()

  if (enableTenantInterceptor) {
    const tenant = store.getTenant()
    config.headers[HEADER_TOKEN_TENANT] = tenant
  }
  if (enableAuthInterceptor) {
    const token = store.getToken()
    config.headers[HEADER_TOKEN_AUTHORIZATION] = `Bearer ${token}`
  }
  config.headers[HEADER_CLIENT_CHANNEL] = "WEB"
  return config
})
